import React, {useState, useEffect, useRef} from 'react';
import {Drawer, FormGroup, InputGroup, Checkbox, Button, Callout} from '@blueprintjs/core';
import styled from 'styled-components';
import {errorToast} from '../../../../utils/toaster';
import * as AuthAPI from '../../../../services/auth-api';

const FormWrapper = styled.form`
  width: 90%;
  margin: 2em auto;
`;
const SmallLabel = styled.p`
  color: #555;
  font-size: 12px;
  margin: 0 0 2em 0;
`;

const getRoleMappings = (selectedOrgName) => ({
    'Analytics data viewer': {
        checkedByDefaultForNewUserInvite: true,
        deleteDisabled: true,
        createDisabled: false,
        label: 'Analytics data viewer',
        description: 'Can access this organisations data on the dashboard (required)',
    },
    'Client Admin - Auth Service Administration': {
        checkedByDefaultForNewUserInvite: false,
        deleteDisabled: false,
        createDisabled: selectedOrgName === 'Demo Company',
        label: 'User Admin',
        description: 'Can view, add, edit and delete users within this organisation (disabled for Demo Company users)',
    },
    'Data Viewer - Auth Service Access': {
        checkedByDefaultForNewUserInvite: true,
        deleteDisabled: true,
        createDisabled: false,
        label: 'Basic user access',
        description: 'Can view and edit their own account (required)',
    },
    'Analytics data viewer - manage reports': {
        checkedByDefaultForNewUserInvite: false,
        deleteDisabled: false,
        createDisabled: selectedOrgName === 'Demo Company',
        label: 'Manage reports',
        description: 'Can create, edit and remove dashboard reports and email report schedules (disabled for Demo Company users)',
    },
    'Client Admin - manage locations': {
        checkedByDefaultForNewUserInvite: false,
        deleteDisabled: false,
        createDisabled: selectedOrgName === 'Demo Company',
        label: 'Manage locations',
        description: 'Can create, edit and remove locations (disabled for Demo Company users)',
    },
    'Client Admin - camera and device set up': {
        checkedByDefaultForNewUserInvite: false,
        deleteDisabled: false,
        createDisabled: selectedOrgName === 'Demo Company',
        label: 'Access to the cameras tool',
        description: 'Can log in to the cameras tool and edit lines and areas',
    },
    'Analytics data viewer - manage campaigns': {
        checkedByDefaultForNewUserInvite: false,
        deleteDisabled: false,
        createDisabled: selectedOrgName === 'Demo Company',
        label: 'Manage campaigns',
        description: 'Can create, edit and remove dashboard campaigns (disabled for Demo Company users)',
    },
});

const InviteUser = ({isOpen, onClose, selectedOrg}) => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inviteResponse, setInviteResponse] = useState({});
    const formRef = useRef(null);
    const emailInput = useRef(null);
    const roleCheckboxes = useRef(null);

    const roleMappings = getRoleMappings(selectedOrg?.name);

    useEffect(() => {
        if (isOpen) {
            if (emailInput.current) emailInput.current.focus();
            setInviteResponse({});
            AuthAPI.roles.list()
                .then(({data}) => setRoles(data))
                .catch(e => console.error(e));
        }
    }, [isOpen]);

    useEffect(() => {
        if (!roleCheckboxes.current) return;
        const checkboxes = roleCheckboxes.current.querySelectorAll('input[type=checkbox]');
        if (!checkboxes) return;
        checkboxes.forEach(cb => {
            const mapping = roleMappings[cb.name];
            if (mapping?.checkedByDefaultForNewUserInvite) cb.checked = true;
        });
    }, [roleMappings, roles]);

    const inviteUser = async (e) => {
        e.preventDefault();

        const email = emailInput.current.value;
        const roleIds = Array.from(
            roleCheckboxes.current.querySelectorAll('input[type=checkbox]:checked')
        ).map(el => el.value);

        if (!roleIds.length) {
            errorToast({message: 'No roles selected'});
            return;
        }

        setLoading(true);
        setInviteResponse({});

        try {
            await AuthAPI.invite.send(email, roleIds, selectedOrg);
            setInviteResponse({type: 'success', text: 'Invite sent to user'});
        } catch {
            setInviteResponse({type: 'warning', text: 'There was an error'});
        }

        setLoading(false);
    };

    return (
        <Drawer
            icon="person"
            title="Add a new user"
            isOpen={isOpen}
            onClose={onClose}
        >
            <FormWrapper ref={formRef} onSubmit={inviteUser}>
                <FormGroup
                    helperText="This needs to be an actual email address - this person will recieve an email to set their password"
                    label="Email address"
                    labelFor="text-input"
                    labelInfo="(required)"
                >
                    <InputGroup
                        id="text-input"
                        required
                        inputRef={emailInput}
                        type="email"
                    />
                </FormGroup>
                <FormGroup label="Organisation" labelFor="text-input">
                    <InputGroup id="text-input" value={selectedOrg?.name || ''} disabled />
                </FormGroup>
                <FormGroup label="Roles">
                    <div ref={roleCheckboxes}>
                        {roles.map(role => {
                            const roleMapping = roleMappings[role.name];
                            if (!roleMapping) return null;
                            return (
                                <div key={role.id}>
                                    <Checkbox
                                        disabled={roleMapping.createDisabled || roleMapping.deleteDisabled}
                                        name={role.name}
                                        value={role.id}
                                    >
                                        {roleMapping.label}
                                    </Checkbox>
                                    <SmallLabel>{roleMapping.description}</SmallLabel>
                                </div>
                            );
                        })}
                    </div>
                </FormGroup>
                <Button type="submit" loading={loading} intent="primary">
                    Add user to this organisation
                </Button>
                {inviteResponse.type && (
                    <Callout intent={inviteResponse.type}>{inviteResponse.text}</Callout>
                )}
            </FormWrapper>
        </Drawer>
    );
};

export {InviteUser};
